import {
  AiNodeQuota,
  BasicModelCard,
  SimulatedQuotasResponse,
  SimulateQuotasRequest,
} from "src/aiNode/types";
import {
  ConnectApiVersions,
  connectApiDelete,
  connectApiGet,
  connectApiPatch,
  connectApiPost,
  connectApiPut,
} from "src/api/connectApi/connectApi";
import {
  ManifestIntegrationProvider,
  ManifestsCatalogResponse,
  ManifestsListResponse,
} from "src/api/connectApi/manifestTypes";
import {
  Connection,
  ConnectionCreate,
  MockTemplateRequest,
  MockTemplateResponse,
  RetoolEmbedQueryParams as QueryPayload,
  ResourceConfig,
  ResourceSample,
  ValidateCustomAuthRequest,
  ValidateCustomAuthResponse,
  ValidateCustomErrorProcessingRequest,
  ValidateCustomErrorProcessingResponse,
} from "src/api/connectApi/types";
import {
  llmInferenceApiGet,
  llmInferenceApiPost,
} from "src/api/llmInferenceApi/llmInferenceApi";
import { S3PreSignedUrlData } from "src/api/s3";
import {
  ManifestConnection,
  ValidateProductionEnvironmentResponse,
} from "src/connections/config/manifest/types";

export class AiEndpoint {
  static readonly all = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<BasicModelCard[]>(
      baseUrl,
      `/ai/models`,
      {},
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };

  static readonly quotas = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<AiNodeQuota>(
      baseUrl,
      `/ai/quotas`,
      {},
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };
}

export class LlmInferenceEndpoint {
  static readonly all = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await llmInferenceApiGet<BasicModelCard[]>(
      baseUrl,
      `/ai/models`,
    );
    return response.data;
  };

  static readonly quotas = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await llmInferenceApiGet<AiNodeQuota>(
      baseUrl,
      `/ai/quotas`,
    );
    return response.data;
  };

  static readonly simulateQuotas = async (
    baseUrl: string | undefined,
    payload: SimulateQuotasRequest,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await llmInferenceApiPost<SimulatedQuotasResponse>(
      baseUrl,
      `/ai/quotas/simulations`,
      payload,
    );
    return response.data;
  };
}

export class ProviderEndpoint {
  static readonly query = async <R = Connection>(
    baseUrl: string | undefined,
    provider: string,
    resource: string,
    payload: QueryPayload,
  ) => {
    if (!baseUrl || !provider || !resource)
      throw new Error("Missing baseUrl, provider or resource");
    const response = connectApiPost<R>(
      baseUrl,
      `/providers/${provider}/resources/${resource}`,
      payload,
    );
    return (await response).data;
  };
}

export class ConnectionsEndpoint {
  static readonly all = async (
    baseUrl?: string,
    params?: {
      include_internal_connections?: boolean;
    },
  ) => {
    if (!baseUrl) return [];
    if (
      params &&
      "include_internal_connections" in params &&
      params.include_internal_connections === false
    ) {
      // Only pass if true so we don't advertise its existance
      delete params.include_internal_connections;
    }
    const response = connectApiGet<Connection[]>(
      baseUrl,
      `/connections`,
      params,
    );
    return (await response).data;
  };

  static readonly get = async (
    baseUrl: string | undefined,
    connectionId: string,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = connectApiGet<Connection>(
      baseUrl,
      `/connections/${connectionId}`,
    );
    return (await response).data;
  };

  static readonly create = async (
    baseUrl: string | undefined,
    data: ConnectionCreate,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = connectApiPost<Connection>(baseUrl, `/connections`, data);
    return (await response).data;
  };

  static readonly createConnectionAndResources = async (
    baseUrl: string,
    data: Pick<
      Connection,
      | "name"
      | "is_sandbox"
      | "secrets"
      | "resource_configs"
      | "provider"
      | "configuration"
      | "data_retention"
      | "disable_credential_bg_jobs"
    >,
  ) => {
    const response = connectApiPost<Connection>(baseUrl, `/connections`, data);
    return (await response).data;
  };

  static readonly delete = async (
    baseUrl: string | undefined,
    connectionId: string,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = connectApiDelete<Connection>(
      baseUrl,
      `/connections/${connectionId}`,
    );
    return (await response).data;
  };

  static readonly update = async (
    baseUrl: string | undefined,
    connectionId: string,
    data: ConnectionCreate,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = connectApiPatch<Connection>(
      baseUrl,
      `/connections/${connectionId}`,
      data,
    );
    return (await response).data;
  };

  static readonly updateConnectionAndResources = async (
    baseUrl: string,
    connectionId: string,
    data: Pick<
      Connection,
      | "name"
      | "is_sandbox"
      | "secrets"
      | "resource_configs"
      | "provider"
      | "configuration"
      | "data_retention"
      | "disable_credential_bg_jobs"
    >,
  ) => {
    const response = connectApiPatch<Connection>(
      baseUrl,
      `/connections/${connectionId}`,
      data,
    );
    return (await response).data;
  };

  static readonly test = async (
    baseUrl: string | undefined,
    connectionId: string | undefined,
  ) => {
    if (!baseUrl || !connectionId) throw new Error("No baseUrl provided");
    return connectApiGet<void>(baseUrl, `/connections/${connectionId}/probe`);
  };

  static readonly getSignedUrlLogo = async (
    baseUrl: string | undefined,
    extension: string,
  ): Promise<S3PreSignedUrlData> => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<S3PreSignedUrlData>(
      baseUrl,
      `/media/file_upload_url?extension=${extension}`,
    );
    return {
      url: response.data.url,
      fields: response.data.fields,
    };
  };
  static readonly validateProductionEnvironment = async (
    baseUrl: string | undefined,
    data: { connection_ids: string[] },
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = connectApiPost<ValidateProductionEnvironmentResponse>(
      baseUrl,
      "/connections:validateProductionEnvironment",
      data,
    );
    return (await response).data;
  };
}

export class ManifestConnectionsEndpoint {
  static readonly create = async (
    baseUrl: string | undefined,
    data: ManifestConnection,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiPost<Connection>(
      baseUrl,
      `/connections`,
      data,
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };

  static readonly update = async (
    baseUrl: string | undefined,
    connectionId: string,
    data: ManifestConnection,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiPut<Connection>(
      baseUrl,
      `/connections/${connectionId}`,
      data,
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };
}

export class ManifestsEndpoint {
  static readonly all = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<ManifestsListResponse>(
      baseUrl,
      `/manifests`,
      { latest: true },
      { version: ConnectApiVersions.V2 },
    );
    return response.data?.data;
  };

  static readonly catalog = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<ManifestsCatalogResponse>(
      baseUrl,
      `/manifests/catalog`,
      {},
      { version: ConnectApiVersions.V2 },
    );
    return response.data?.data;
  };

  static readonly local = async (baseUrl?: string) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<ManifestsCatalogResponse>(
      baseUrl,
      `/manifests/local`,
      {},
      { version: ConnectApiVersions.V2 },
    );
    return response.data?.data;
  };

  static readonly get = async (
    provider: string,
    manifest_version?: string,
    baseUrl?: string,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiGet<ManifestIntegrationProvider>(
      baseUrl,
      `/manifests/${provider}`,
      { manifest_version: manifest_version },
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };
}

export class ResourceConfigsEndpoint {
  static readonly get = async (
    baseUrl: string | undefined,
    connectionId: string,
    resourceConfigId: string,
  ) => {
    if (baseUrl === undefined) throw Error("Missing connect base url");
    const response = connectApiGet<ResourceConfig>(
      baseUrl,
      `/connections/${connectionId}/resources/${resourceConfigId}`,
    );
    return (await response).data;
  };
}

export class ResourceSamplesEndpoint {
  static readonly get = async (
    baseUrl: string,
    provider: string,
    resource: string,
  ) => {
    const response = connectApiGet<ResourceSample[]>(
      baseUrl,
      `/providers/${provider}/resources/${resource}/samples`,
    );
    return (await response).data;
  };
}

export class IntegrationBuilderEndpoint {
  static readonly mockTemplate = async (
    baseUrl: string | undefined,
    data: MockTemplateRequest,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiPost<MockTemplateResponse>(
      baseUrl,
      `/tools/mock`,
      data,
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };

  static readonly validateCustomAuth = async (
    baseUrl: string | undefined,
    data: ValidateCustomAuthRequest,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response = await connectApiPost<ValidateCustomAuthResponse>(
      baseUrl,
      `/manifest/:validateCustomAuth`,
      data,
      { version: ConnectApiVersions.V2 },
    );
    return response.data;
  };

  static readonly validateCustomErrorProcessing = async (
    baseUrl: string | undefined,
    data: ValidateCustomErrorProcessingRequest,
  ) => {
    if (!baseUrl) throw new Error("No baseUrl provided");
    const response =
      await connectApiPost<ValidateCustomErrorProcessingResponse>(
        baseUrl,
        `/manifest/:validateCustomErrorProcessing`,
        data,
        { version: ConnectApiVersions.V2 },
      );
    return response.data;
  };
}
